/**
 * How a displayed item finds its sources. ONE rule, one place.
 *
 * THE RULE IS: exact `claimKey` equality, and nothing else.
 *
 * Every one of the five report bodies used to invent its own join, and four of
 * the five were wrong in a way no test caught:
 *
 *   Porter matched `methodologyElement === `${force}.${driver}`` against a
 *   stored `driver.${force}.${driver}`. The `driver.` prefix was missing, so
 *   `find` returned undefined for every driver on every run and no Porter
 *   observation in production has ever had a route to its sources.
 *
 *   PESTEL matched `statement === item.summary` against a category-level claim
 *   whose statement is "The legal posture is adverse, from 3 supported item(s)."
 *   Those two strings can never be equal, so no PESTEL item has ever had one
 *   either.
 *
 *   Market did not join at all. It rendered every claim as a flat list beside
 *   the figures and left the reader to guess which statement belonged to which
 *   number.
 *
 *   Exit matched `statement.includes(candidate.name)`. That one worked, right
 *   up until two candidates share a prefix — "Meta" and "Meta Platforms" — at
 *   which point `find` returns whichever claim comes first and one company's
 *   sources are shown under the other's name.
 *
 * The report now carries an exact key on every material node (schema 1.1.0), so
 * none of that reconstruction is needed. A component that cannot find a claim
 * for a key renders no source control, which is the honest outcome: the claim
 * was rejected at validation and there is nothing to open.
 */

import type { ClaimView } from '@/lib/strategic-analysis/types';

/**
 * The claim for one displayed node, by its exact key.
 *
 * No fallback. A near-miss fallback is how a wrong citation gets attached
 * confidently — and a confident wrong citation is worse than a missing one,
 * because the reader has no way to tell.
 */
export function claimFor(claims: readonly ClaimView[], claimKey: string | undefined): ClaimView | null {
  if (!claimKey) return null;
  return claims.find((claim) => claim.claimKey === claimKey) ?? null;
}

/**
 * An index, for bodies that resolve many keys in one render.
 *
 * Same rule, built once instead of scanning the array per node.
 */
export function claimIndex(claims: readonly ClaimView[]): ReadonlyMap<string, ClaimView> {
  const index = new Map<string, ClaimView>();
  for (const claim of claims) {
    if (!index.has(claim.claimKey)) index.set(claim.claimKey, claim);
  }
  return index;
}
